import React, { useEffect, useState } from "react";

import {
  TextField,
  MenuItem,
  Typography,
  Box
} from "@mui/material";

import {
  useForm
} from "react-hook-form";

import {
  getDepartments
} from "../../services/departmentService";
import {
  getEmployees
} from "../../services/employeeService";

const roleOptions = [
  { value: "super_admin", label: "Super Admin" },
  { value: "manager", label: "Manager" },
  { value: "employee", label: "Employee" },
  { value: "member", label: "Member" }
];

function EmployeeForm({
  employee,
  onSubmit
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);

  const loadDepartments = async () => {
    try {
      const data = await getDepartments();
      setDepartments(data);
    }
    catch (error) {
      console.error("Department Error:", error);
    }
  };

  const loadManagers = async () => {
    try {
      const data = await getEmployees();
      setManagers(data);
    }
    catch (error) {
      console.error("Manager Load Error:", error);
    }
  };

  useEffect(() => {
    loadDepartments();
    loadManagers();

    if (employee) {
      reset({
        employee_name: employee.employee_name,
        email: employee.email,
        designation: employee.designation || "",
        department_id: employee.department_id,
        role_name: employee.role_name || "member",
        manager_id: employee.manager_id || ""
      });
    }
    else {
      reset({
        employee_name: "",
        email: "",
        designation: "",
        department_id: "",
        role_name: "member",
        manager_id: ""
      });
    }
  }, [employee, reset]);

  return (
    <form
      id="employee-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.2
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: "#475569",
            fontWeight: 600,
            mb: 0.2
          }}
        >
          Employee Information
        </Typography>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Role
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            disabled={Boolean(employee)}
            defaultValue={employee?.role_name || "member"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              }
            }}
            {...register("role_name")}
          >
            {roleOptions.map((roleOption) => (
              <MenuItem key={roleOption.value} value={roleOption.value}>
                {roleOption.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Manager <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={employee?.manager_id || ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              }
            }}
            {...register("manager_id")}
          >
            <MenuItem value="">No manager</MenuItem>

            {managers
              .filter((manager) => manager.employee_id !== employee?.employee_id)
              .map((manager) => (
                <MenuItem
                  key={manager.employee_id}
                  value={manager.employee_id}
                >
                  {manager.employee_name}
                </MenuItem>
              ))}
          </TextField>
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Employee Name
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter employee name"
            disabled={Boolean(employee)}
            error={Boolean(errors.employee_name)}
            helperText={errors.employee_name ? "Employee name is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("employee_name", { required: true })}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Email
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter email address"
            disabled={Boolean(employee)}
            error={Boolean(errors.email)}
            helperText={errors.email ? "Email is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("email", { required: true })}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Designation
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter designation / job title"
            disabled={Boolean(employee)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              }
            }}
            {...register("designation")}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#334155",
              mb: 0.8
            }}
          >
            Department <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            defaultValue={employee?.department_id || ""}
            error={Boolean(errors.department_id)}
            helperText={errors.department_id ? "Please select a department" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#ffffff"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("department_id", { required: true })}
          >
            <MenuItem value="" disabled>
              Select department
            </MenuItem>

            {departments.map((department) => (
              <MenuItem
                key={department.department_id}
                value={department.department_id}
              >
                {department.department_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </form>
  );
}

export default EmployeeForm;
